import {
  DynamicFee,
  FeeAppliesTo,
  FeeChargeBasis,
  FeeConditionOperator,
  FeeEligibilityCondition,
  FeeEligibilityScope,
  FeeType,
} from 'types/incentive';
import { getStoreCurrencyFormatter } from 'utils';

const normalizedText = (value: unknown) =>
  typeof value === 'string' ? value.trim().toLocaleUpperCase() : '';

const conditionValues = (condition: FeeEligibilityCondition) => {
  const value = typeof condition?.value === 'string' ? condition.value : '';
  return condition?.operator === FeeConditionOperator.IN
    ? value
        .split(/[\n,]/)
        .map(normalizedText)
        .filter(Boolean)
    : [normalizedText(value)];
};

export const doesSkuMatchFeeCondition = (
  condition: FeeEligibilityCondition,
  sku: string,
) => {
  const candidate = normalizedText(sku);
  if (!candidate) return false;

  const values = conditionValues(condition);
  const [configuredValue] = values;
  if (!configuredValue) return false;

  switch (condition.operator) {
    case FeeConditionOperator.EQUALS:
      return candidate === configuredValue;
    case FeeConditionOperator.STARTS_WITH:
      return candidate.startsWith(configuredValue);
    case FeeConditionOperator.ENDS_WITH:
      return candidate.endsWith(configuredValue);
    case FeeConditionOperator.CONTAINS:
      return candidate.includes(configuredValue);
    case FeeConditionOperator.IN:
      return values.includes(candidate);
    default:
      return false;
  }
};

export const doesDynamicFeeMatchSampleSku = (
  fee: DynamicFee,
  sku: string,
) => {
  if (!normalizedText(sku)) return false;
  if (fee.eligibility?.scope !== FeeEligibilityScope.MATCHING_ITEMS) {
    return true;
  }

  const conditions = fee.eligibility.conditions || [];
  return (
    conditions.length > 0 &&
    conditions.every((condition) => doesSkuMatchFeeCondition(condition, sku))
  );
};

const quoted = (value: string) => `“${value.trim()}”`;

export const summarizeFeeCondition = (
  condition: FeeEligibilityCondition,
) => {
  if (typeof condition?.value !== 'string' || !condition.value.trim()) {
    return 'Incomplete SKU condition';
  }

  switch (condition.operator) {
    case FeeConditionOperator.EQUALS:
      return `SKU equals ${quoted(condition.value)}`;
    case FeeConditionOperator.STARTS_WITH:
      return `SKU starts with ${quoted(condition.value)}`;
    case FeeConditionOperator.ENDS_WITH:
      return `SKU ends with ${quoted(condition.value)}`;
    case FeeConditionOperator.CONTAINS:
      return `SKU contains ${quoted(condition.value)}`;
    case FeeConditionOperator.IN: {
      const values = condition.value
        .split(/[\n,]/)
        .map((value) => value.trim())
        .filter(Boolean)
        .map(quoted);
      return values.length > 0
        ? `SKU is one of ${values.join(', ')}`
        : 'Incomplete SKU condition';
    }
    default:
      return 'SKU condition';
  }
};

export const summarizeFeeConditions = (fee: DynamicFee) => {
  if (fee.eligibility?.scope !== FeeEligibilityScope.MATCHING_ITEMS) {
    return 'all returns';
  }

  const conditions = fee.eligibility.conditions || [];
  return conditions.length > 0
    ? conditions.map(summarizeFeeCondition).join(' and ')
    : 'matching items';
};

const describeFeeOutcome = (appliesTo?: FeeAppliesTo) => {
  switch (appliesTo) {
    case FeeAppliesTo.STORE_CREDIT:
      return 'store credit';
    case FeeAppliesTo.BOTH:
      return 'refunds and store credit';
    case FeeAppliesTo.REFUND:
    default:
      return 'refunds';
  }
};

export const summarizeDynamicFeeRule = (
  currency: string,
  fee: DynamicFee,
) => {
  const amount =
    fee.feeType === FeeType.PERCENTAGE
      ? `${new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
        }).format(fee.amount)}%`
      : getStoreCurrencyFormatter(currency, fee.amount);
  const scope = summarizeFeeConditions(fee);
  const chargeBasis =
    fee.chargeBasis === FeeChargeBasis.PER_MATCHING_UNIT
      ? 'per matching unit'
      : 'once per return';

  return `${amount} from ${describeFeeOutcome(
    fee.appliesTo,
  )} ${scope === 'all returns' ? 'on' : 'when'} ${scope}, ${chargeBasis}.`;
};
